import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

const IPOwnersPage = () => {
  useEffect(() => {
    document.title = 'For IP Owners | NEXUS LBE';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <Badge variant="outline">For IP Owners</Badge>
              <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Transform your IP into scalable, emotionally resonant location based experiences
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our global formatted platform brings your titles to life and unlocks new revenue streams
              </p>
            </div>
            <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-secondary shadow-2xl">
              <img 
                src="/images/ip-owners-hero-v2.png" 
                alt="Scale your titles with NEXUS LBE"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Why IP owners choose NEXUS LBE
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Scalable, repeatable, and built for your brand's unique identity
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Box 1 - Nexus LB Formatted */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Nexus LB Formatted: A scalable model
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Through our formatted approach we can launch in new cities fast to keep the cost for new titles down and benefit from existing operational excellence. Essentially, hybrid LBE.
                </p>
              </CardContent>
            </Card>

            {/* Box 2 - Consumer Fatigue */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Consumer Fatigue
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our model lessens consumer fatigue as we will debut different LBE experiences at each site, allowing us to cater to not just tourist audiences but the more durable and populous domestic audiences as well
                </p>
              </CardContent>
            </Card>

            {/* Box 3 - Recycling Titles */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Recycling Titles + Revenue streams
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  If one title underperforms, we will shorten the run time and have a new title ready to go with less development / launch time required. We also have multiple revenue streams eg tickets, merch food and bev
                </p>
              </CardContent>
            </Card>

            {/* Box 4 - Narrative Experience */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Narrative Experience
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our Formatted model has narrative at the forefront of the experience, means we can reduce Tech costs and create Playbooks for each title ensuring easy scaling eg actor driven
                </p>
              </CardContent>
            </Card>

            {/* Box 5 - Strong IP Pipeline */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Strong IP pipeline
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Through partnering with you and your company, we will always have fresh and new titles and can work with your marketing cycles for big films, TV shows, games and even product launches
                </p>
              </CardContent>
            </Card>

            {/* Box 6 - Collaborative Partner Ecosystem */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <h3 className="font-['Geist'] text-xl font-semibold">
                  Collaborative Partner Ecosystem
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We work hand in hand with a range of partners / suppliers to deliver our Formatted model (property, ticketing, IT, etc) - we are creating a one stop shop for LBEs making us easy to work with
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Standardized vs Bespoke */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Standardized Where It Matters. Bespoke Where It Counts.
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Balancing reliability with innovation – build fast, delight deeply
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <h3 className="font-['Geist'] text-2xl mb-6 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  </div>
                  The Platform (Standardized)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>5-Phase Format:</strong> Scalable construction for easy roll-out and consistency</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>Proven Psychology:</strong> Peak-end rule drives success (90% of an experience is judged by the emotional peaks)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>Standardized Media Tech:</strong> For ease of use mixed with live action theatrical</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Platform handles the 'how': flow, monetization, immersion</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8">
                <h3 className="font-['Geist'] text-2xl mb-6 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  The Experience (Bespoke)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>Title-Specific Creative:</strong> Each title receives bespoke creative to ensure its unique appeal</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>Seamless Execution:</strong> Leverages our platform for consistent delivery</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>IP-Specific Magic:</strong> Customization delivers the "wow" moment</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span><strong>Emotional Fit:</strong> Narrative and experience tailored to your brand's DNA</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Ready to discuss your IP?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Let's explore how NEXUS LBE can bring your titles to life in immersive, scalable experiences.
          </p>
          <Link to="/contact">
            <Button size="lg" className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IPOwnersPage;
